import React from "react";
import { useNavigate } from "react-router-dom";
import { WebPaths } from "../../../../routes/data";
import Button from "../../components/ui/Button";
import {
  FaArrowRight,
  FaCheck,
  FaGraduationCap,
  FaPassport,
  FaRobot,
} from "react-icons/fa6";
import { CA, GB, IE, AU, US } from "country-flag-icons/react/3x2";
import { motion } from "framer-motion";

const services = [
  {
    headline: "Study Abroad",
    tagline: "Your path to world-class education starts here",
    description:
      "Unlock doors to prestigious institutions across five countries. Our expert advisors, study abroad partners, and extensive university network ensure you secure admission to a school that aligns with your goal and budget.",
    icon: <FaGraduationCap className="text-xl" />,
    theme: {
      cardBg: "bg-blue-50",
      text: "text-blue-600 group-hover/card:text-blue-800",
      border: "border-blue-100 hover:border-blue-300",
      shadow: "shadow-sm hover:shadow-blue-500/10",
      check: "text-blue-500",
    },
    buttonVariant: "primary",
    ctaText: "Learn More About Study Abroad",
    ctaPath: WebPaths.Academy,
    visual: (
      <div className="mt-8 flex flex-col gap-4">
        <div className="flex gap-2">
          {[CA, GB, IE, AU, US].map((Flag, i) => (
            <div
              key={i}
              className="h-6 w-8 overflow-hidden rounded-[2px] shadow-sm border border-gray-100/50"
            >
              <Flag className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
        <div className="space-y-3 mt-4">
          {[
            "Personalized university matching",
            "Application strategy and review",
            "Essay and SOP guidance",
            "Interview preparation coaching",
            "Scholarship identification support",
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-3 text-sm text-gray-700 font-normal"
            >
              <FaCheck
                className="mt-1 h-3.5 w-3.5 shrink-0 text-blue-500"
                aria-hidden="true"
              />
              <span className="leading-snug">{item}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    headline: "Intelligent Visa Processing",
    tagline: "Technology meets expertise for visa success",
    description:
      "Navigate the complexities of visa applications with confidence. Take advantage of our dynamic interview preparation to maximize your approval chances.",
    icon: <FaPassport className="text-xl" />,
    theme: {
      cardBg: "bg-emerald-50",
      text: "text-emerald-600 group-hover/card:text-emerald-800",
      border: "border-emerald-100 hover:border-emerald-300",
      shadow: "shadow-sm hover:shadow-emerald-500/10",
      check: "text-emerald-500",
    },
    buttonVariant: "secondary",
    ctaText: "Explore our Visa Services",
    ctaPath: WebPaths.Individuals,
    visual: (
      <div className="mt-8 flex flex-col justify-center min-h-[140px] rounded-xl bg-white/60 border border-emerald-100/50 p-5 hidden transition-colors duration-500 lg:flex group-hover/card:bg-white/80">
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-emerald-200/50 pb-2">
            <span className="text-[10px] uppercase tracking-wider text-emerald-700">
              Live Analysis
            </span>
          </div>
          <div className="h-2 w-3/4 rounded bg-emerald-200" />
          <div className="h-2 w-1/2 rounded bg-emerald-200" />
          <div className="h-2 w-5/6 rounded bg-emerald-200" />
          <div className="flex items-center gap-3 pt-4">
            <div className="h-8 w-24 rounded-full bg-emerald-100 flex items-center justify-center">
              <div className="h-2 w-16 bg-emerald-400 rounded" />
            </div>
            <div className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs">
              <FaCheck />
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    headline: "FiBi – Your Personal Interview Trainer",
    tagline: "Practice Today. Impress Tomorrow.",
    description:
      "Practice with our dynamic U.S. visa interview simulator. Get real-time feedback and build confidence for the actual consular or admissions interview.",
    icon: <FaRobot className="text-xl" />,
    theme: {
      cardBg: "bg-purple-50",
      text: "text-purple-600 group-hover/card:text-purple-800",
      border: "border-purple-100 hover:border-purple-300",
      shadow: "shadow-sm hover:shadow-purple-500/10",
      check: "text-purple-500",
    },
    buttonVariant: "fibi",
    ctaText: "Test-Drive FiBi",
    ctaPath: WebPaths.FiBiAi,
    visual: (
      <div className="mt-8 space-y-3">
        {[
          "AI-powered Dynamic consular interview simulator",
          "Intelligent U.S. Visa-Category-Specific Question Generator",
          "AI-powered Dual-Mode Interview Simulation — Text & Voice",
          "Real-Time AI Response Evaluation & Feedback Engine",
        ].map((item, i) => (
          <div
            key={i}
            className="flex items-start gap-3 text-sm text-gray-700 font-normal"
          >
            <FaCheck
              className="mt-1 h-3.5 w-3.5 shrink-0 text-purple-500"
              aria-hidden="true"
            />
            <span className="leading-snug">{item}</span>
          </div>
        ))}
      </div>
    ),
  },
];

const ServicesOverview = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-light tracking-tight text-gray-900 sm:text-5xl font-spartan mb-6 flex flex-col items-center gap-4">
            <span>Your Complete Global</span>
            <span className="text-blue-00 italic">Mobility Solution</span>
          </h2>
          <p className="mt-4 text-lg leading-8 text-gray-600 font-normal">
            From dream school acceptance to visa approval. We're with you at
            every milestone
          </p>
        </div>

        {/* Components Grid */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {services.map((service, index) => (
            <motion.div
              key={service.headline}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`group/card relative flex flex-col overflow-hidden rounded-3xl p-8 transition-colors duration-500 border ${service.theme.cardBg} ${service.theme.border} ${service.theme.shadow}`}
            >
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors duration-500 bg-white shadow-sm border border-white/50 ${service.theme.text}`}
              >
                {service.icon}
              </div>

              <div className="mt-6">
                <h3 className="text-xl font-normal text-gray-900 font-spartan">
                  {service.headline}
                </h3>
                <p className="mt-2 text-sm font-normal text-gray-500 italic">
                  {service.tagline}
                </p>
                <p className="mt-5 text-base font-normal text-gray-700 leading-relaxed">
                  {service.description}
                </p>
              </div>

              <div className="mt-auto grow pt-4">{service.visual}</div>

              <div className="mt-8">
                <Button
                  label={service.ctaText}
                  onClick={() => navigate(service.ctaPath)}
                  variant={service.buttonVariant as any}
                  fullWidth
                  // size="large"
                  endIcon={<FaArrowRight className="h-3 w-3" />}
                  iconHoverStyle="scale"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;
